import React, { useState, useEffect, useCallback } from "react";
import apiService from "../services/apiService";
import { handleDeleteError, handleUpdateError, handleCreateError } from "../utils/errorHandler";
import {
  Box,
  Container,
  Tooltip,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  FormControl,
  InputLabel,
  Select,
  CssBaseline,
  Paper,
  Popover,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";

function AgingDetails() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const getToday = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const today = getToday();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [exportError, setExportError] = useState("");

  const [filters, setFilters] = useState({
    date_from: today,
    date_to: today,
    env_id: "",
    app_id: "",
  });

  const handleExport = async () => {
    try {
      setExportError("");

      const params = new URLSearchParams({
        date_from: exportFilters.date_from,
        date_to: exportFilters.date_to,
      });

      if (exportFilters.env_id) params.append("env_id", exportFilters.env_id);

      if (exportFilters.app_id) params.append("app_id", exportFilters.app_id);

      const response = await fetch(
        `${BASE_URL}/aging-orders/export/csv?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to export file");
      }

      const text = await response.text();

      // Split rows
      const rows = text.split("\n").filter((row) => row.trim() !== "");

      if (rows.length <= 1) {
        setExportError("No data found for this date range");
        return;
      }

      const blob = new Blob([text], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const envPart = exportFilters.env_id ? `_${exportFilters.env_id}` : "";
      const appPart = exportFilters.app_id ? `_${exportFilters.app_id}` : "";

      a.download = `Aging_Report${envPart}${appPart}_${exportFilters.date_from}_to_${exportFilters.date_to}.csv`;

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setOpenExportDialog(false);
    } catch (error) {
      setExportError(error.message || "Export failed");
    }
  };

  const [clients, setClients] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [filteredLogicalGroups, setFilteredLogicalGroups] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState({
    aging_id: false,
    client_name: true,
    environment_name: true,
    application_name: true,
    logical_group_name: true,
    aging_count: true,
    pkms_count: true,
    ca_missing_count: true,
    date: true,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    aging_id: "",
    cl_id: "",
    client_name: "",
    env_id: "",
    app_id: "",
    lg_id: "",
    aging_count: "",
    pkms_count: "",
    ca_missing_count: "",
    date: today,
  });
  const [originalFormData, setOriginalFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    env_id: "",
    app_id: "",
    date_from: today,
    date_to: today,
  });

  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);

  // Column filter states
  const [columnFilters, setColumnFilters] = useState({});
  const [tempColumnFilters, setTempColumnFilters] = useState({});
  const [filterAnchorEls, setFilterAnchorEls] = useState({});

  const columnLabels = {
    aging_id: "Aging ID",
    client_name: "Client",
    environment_name: "Environment",
    application_name: "Application",
    logical_group_name: "Logical Group",
    aging_count: "Aging Count",
    pkms_count: "PKMS Count",
    ca_missing_count: "CA Missing Count",
    date: "Date",
  };

  const columns = Object.keys(columnLabels);

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(dateString));
  };

  const fetchOptions = useCallback(async () => {
    try {
      const [clientsData, envData] = await Promise.all([
        apiService.get("/aging-orders/clients"),
        apiService.get("/aging-orders/environments"),
      ]);

      setClients(clientsData);
      setEnvironments(envData);
    } catch (err) {
      console.error("Error loading dropdown options:", err);
    }
  }, []);

  // Cascading validation functions
  const fetchApplicationsByEnvironment = async (envId) => {
    if (!envId) {
      setFilteredApplications([]);
      return;
    }
    try {
      const result = await apiService.get(
        `/aging-orders/applications?env_id=${envId}`,
      );
      setFilteredApplications(result || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
      setFilteredApplications([]);
    }
  };

  const fetchLogicalGroupsByEnvironmentAndApp = async (envId, appId) => {
    if (!envId || !appId) {
      setFilteredLogicalGroups([]);
      return;
    }
    try {
      const result = await apiService.get(
        `/aging-orders/logical-groups?env_id=${envId}&app_id=${appId}`,
      );
      setFilteredLogicalGroups(result || []);
    } catch (error) {
      console.error("Error fetching logical groups:", error);
      setFilteredLogicalGroups([]);
    }
  };

  const handleEnvironmentChange = async (event) => {
    const envId = event.target.value;
    setFormData((prev) => ({
      ...prev,
      env_id: envId,
      app_id: "", // Clear application when environment changes
      lg_id: "", // Clear logical group when environment changes
    }));

    // Clear dependent dropdowns
    setFilteredApplications([]);
    setFilteredLogicalGroups([]);

    // Fetch applications for the selected environment
    if (envId) {
      await fetchApplicationsByEnvironment(envId);
    }
  };

  const handleApplicationChange = async (event) => {
    const appId = event.target.value;
    setFormData((prev) => ({
      ...prev,
      app_id: appId,
      lg_id: "", // Clear logical group when application changes
    }));

    // Clear logical groups
    setFilteredLogicalGroups([]);

    // Fetch logical groups for the selected environment and application
    if (formData.env_id && appId) {
      await fetchLogicalGroupsByEnvironmentAndApp(formData.env_id, appId);
    }
  };

  const handleLogicalGroupChange = (event) => {
    const lgId = event.target.value;
    setFormData((prev) => ({
      ...prev,
      lg_id: lgId,
    }));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      page: page.toString(),
      limit: rowsPerPage.toString(),
    });

    if (filters.date_from) params.append("date_from", filters.date_from);
    if (filters.date_to) params.append("date_to", filters.date_to);
    if (filters.env_id) params.append("env_id", filters.env_id);
    if (filters.app_id) params.append("app_id", filters.app_id);

    // Add column filters
    Object.keys(columnFilters).forEach((key) => {
      if (columnFilters[key]) {
        params.append(key, columnFilters[key]);
      }
    });

    try {
      const result = await apiService.get(
        `/aging-orders/paginate/list?${params}`,
      );
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Error fetching data:", err);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, columnFilters]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Track form changes for edit mode
  useEffect(() => {
    if (isEditing && Object.keys(originalFormData).length > 0) {
      const hasChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormChanged(hasChanged);
    }
  }, [formData, originalFormData, isEditing]);

  const validate = () => {
    const errors = {};
    if (!formData.cl_id) errors.cl_id = "Client is required";
    if (!formData.env_id) errors.env_id = "Environment is required";
    if (!formData.app_id) errors.app_id = "Application is required";
    if (!formData.lg_id) errors.lg_id = "Logical Group is required";
    if (!formData.date) errors.date = "Date is required";

    // Validate that at least one count is provided and greater than 0
    const agingCount = parseInt(formData.aging_count) || 0;
    const pkmsCount = parseInt(formData.pkms_count) || 0;
    const caMissingCount = parseInt(formData.ca_missing_count) || 0;

    if (agingCount === 0 && pkmsCount === 0 && caMissingCount === 0) {
      errors.general =
        "At least one count (Aging, PKMS, or CA Missing) must be greater than 0";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;


    // Clean the data before sending - convert empty strings to 0 (for NOT NULL columns)
    const cleanedData = {
      ...formData,
      aging_count: formData.aging_count ? parseInt(formData.aging_count) : 0,
      pkms_count: formData.pkms_count ? parseInt(formData.pkms_count) : 0,
      ca_missing_count: formData.ca_missing_count
        ? parseInt(formData.ca_missing_count)
        : 0,
    };

    try {
      if (isEditing) {
        await apiService.put(`/aging-orders/${formData.aging_id}`, cleanedData);
      } else {
        await apiService.post("/aging-orders", cleanedData);
      }

      setSuccessMessage(
        isEditing
          ? "Record updated successfully!"
          : "Record added successfully!",
      );
      setOpenDialog(false);
      resetForm();
      fetchData();
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (err) {
      // Use centralized error handler
      const errorMessage = isEditing 
        ? handleUpdateError(err, "Aging Record")
        : handleCreateError(err, "Aging Record");
      setFormErrors({ general: errorMessage });
      setTimeout(() => setFormErrors({}), 3000);
    }
  };

  const resetForm = () => {
    // Set default client when resetting form
    let initialClient = {};
    if (clients.length > 0) {
      const first = clients[0];
      initialClient = {
        cl_id: first.cl_id,
        client_name: first.name || "",
      };
    }

    setFormData({
      aging_id: "",
      ...initialClient,
      env_id: "",
      app_id: "",
      lg_id: "",
      aging_count: "",
      pkms_count: "",
      ca_missing_count: "",
      date: today,
    });
    setIsEditing(false);
    setFormErrors({});
    setFilteredApplications([]);
    setFilteredLogicalGroups([]);
  };

  // Update client when clients are loaded and form is open for adding
  useEffect(() => {
    if (
      clients.length > 0 &&
      (!formData.cl_id || !clients.find((c) => c.cl_id === formData.cl_id))
    ) {
      const first = clients[0];
      setFormData((prev) => ({
        ...prev,
        cl_id: first.cl_id,
        client_name: first.name || "",
      }));
    }
  }, [clients, formData.cl_id]);

  const handleEdit = async (row) => {
    // Find the client name for the row's cl_id
    const client = clients.find((c) => c.cl_id === row.cl_id);
    const clientName = client ? client.name : row.cl_id;

    const editFormData = {
      ...row,
      client_name: clientName, // Set the client name for display
      date: row.date ? row.date.split("T")[0] : today,
    };

    setFormData(editFormData);
    setOriginalFormData({ ...editFormData }); // Store original data for comparison
    setIsFormChanged(false); // Reset change flag
    setIsEditing(true);

    // Load cascading data for editing
    if (row.env_id) {
      await fetchApplicationsByEnvironment(row.env_id);

      if (row.app_id) {
        await fetchLogicalGroupsByEnvironmentAndApp(row.env_id, row.app_id);
      }
    }

    setOpenDialog(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await apiService.delete(`/aging-orders/${deleteId}`);
      setOpenDeleteDialog(false);
      setDeleteId(null);
      fetchData();
      setSuccessMessage("Record deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (err) {
      setOpenDeleteDialog(false);
      setDeleteId(null);
      // Use centralized error handler
      const errorMessage = handleDeleteError(err, "Aging Record");
      setFormErrors({ general: errorMessage });
      setTimeout(() => setFormErrors({}), 5000);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };

  const handleToggleFlag = async (id, currentFlag) => {
    try {
      await apiService.put(`/aging-orders/${id}`, {
        active_flag: currentFlag === 1 ? 0 : 1,
      });
      fetchData();
      
      // Show success message
      const message = currentFlag === 0 
        ? "Aging Record Activated" 
        : "Aging Record Deactivated";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      // Use centralized error handler
      const errorMessage = handleUpdateError(err, "Aging Record");
      setFormErrors({ general: errorMessage });
      setTimeout(() => setFormErrors({}), 3000);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  // Column filter handlers
  const handleFilterIconClick = (event, columnKey) => {
    setFilterAnchorEls((prev) => ({
      ...prev,
      [columnKey]: event.currentTarget,
    }));
    setTempColumnFilters((prev) => ({
      ...prev,
      [columnKey]: columnFilters[columnKey] || "",
    }));
  };

  const handleFilterClose = (columnKey) => {
    setFilterAnchorEls((prev) => ({
      ...prev,
      [columnKey]: null,
    }));
  };

  const applyColumnFilter = (columnKey) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]: tempColumnFilters[columnKey],
    }));
    setPage(1);
    handleFilterClose(columnKey);
  };

  const clearColumnFilter = (columnKey) => {
    setColumnFilters((prev) => {
      const updated = { ...prev };
      delete updated[columnKey];
      return updated;
    });
    setTempColumnFilters((prev) => ({
      ...prev,
      [columnKey]: "",
    }));
    setPage(1);
    handleFilterClose(columnKey);
  };

  // Cascading validation for main filters
  const handleFilterEnvironmentChange = async (event) => {
    const envId = event.target.value;
    setFilters((prev) => ({
      ...prev,
      env_id: envId,
      app_id: "", // Clear application when environment changes
    }));

    // Clear and fetch applications for the selected environment
    setFilteredApplications([]);
    if (envId) {
      await fetchApplicationsByEnvironment(envId);
    }
    setPage(1);
  };

  const handleFilterApplicationChange = (event) => {
    const appId = event.target.value;
    setFilters((prev) => ({
      ...prev,
      app_id: appId,
    }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      date_from: today,
      date_to: today,
      env_id: "",
      app_id: "",
    });
    setFilteredApplications([]); // Clear filtered applications when clearing filters
    setPage(1);
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Filters on left, Action Buttons + Pagination on right */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Left side: Filters */}
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <Box sx={{ position: "relative", minWidth: 140 }}>
            <TextField
              label="Date From"
              type="date"
              size="small"
              value={filters.date_from}
              onChange={(e) => handleFilterChange("date_from", e.target.value)}
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: "0.75rem",
                  top: "1px",
                },
              }}
              sx={{
                minWidth: 140,
                "& .MuiInputBase-root": { height: 37 },
                "& .MuiInputBase-input": {
                  padding: "8px 10px",
                  fontSize: "0.75rem",
                  color: "transparent", // hide browser date
                },
              }}
            />

            {filters.date_from && (
              <Typography
                sx={{
                  mt: -0.5,
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-10%)",
                  fontSize: "0.75rem",
                  color: "#000",
                  pointerEvents: "none",
                }}
              >
                {formatDisplayDate(filters.date_from)}
              </Typography>
            )}
          </Box>

          <Box sx={{ position: "relative", minWidth: 140 }}>
            <TextField
              label="Date To"
              type="date"
              size="small"
              value={filters.date_to}
              onChange={(e) => handleFilterChange("date_to", e.target.value)}
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: "0.75rem",
                  top: "1px",
                },
              }}
              sx={{
                minWidth: 140,
                "& .MuiInputBase-root": { height: 37 },
                "& .MuiInputBase-input": {
                  padding: "8px 10px",
                  fontSize: "0.75rem",
                  color: "transparent",
                },
              }}
            />

            {filters.date_to && (
              <Typography
                sx={{
                  mt: -0.5,
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-10%)",
                  fontSize: "0.75rem",
                  color: "#000",
                  pointerEvents: "none",
                }}
              >
                {formatDisplayDate(filters.date_to)}
              </Typography>
            )}
          </Box>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>Environment</InputLabel>
            <Select
              value={filters.env_id}
              label="Environment"
              onChange={handleFilterEnvironmentChange}
              sx={{
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiSelect-select": {
                  paddingY: "8px !important",
                  fontSize: "0.75rem",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "0.75rem",
                      minHeight: 32,
                    },
                  },
                },
              }}
            >
              {environments.map((env) => (
                <MenuItem
                  key={env.env_id}
                  value={env.env_id}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {env.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>Application</InputLabel>
            <Select
              value={filters.app_id}
              label="Application"
              onChange={handleFilterApplicationChange}
              sx={{
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiSelect-select": {
                  paddingY: "8px !important",
                  fontSize: "0.75rem",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "0.75rem",
                      minHeight: 32,
                    },
                  },
                },
              }}
            >
              {filteredApplications.map((app) => (
                <MenuItem
                  key={app.app_id}
                  value={app.app_id}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {app.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Add + */}
            <Button
              onClick={() => {
                resetForm();
                setOpenDialog(true);
              }}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {/* Clear Filters */}
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={handleClearFilters}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Export */}
            <Tooltip title="Export to CSV">
              <IconButton
                // onClick={() => setOpenExportDialog(true)}
                onClick={() => {
                  setExportError("");
                  setOpenExportDialog(true);
                }}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <FileDownloadIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                total,
              )} of ${total}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(total / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(total / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {formErrors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formErrors.general}
        </Alert>
      )}

      {/* Table */}
      <Box
        sx={{
          height: "calc(90vh - 160px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 230px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {columnLabels[col]}
                              <IconButton
                                size="small"
                                onClick={(e) => handleFilterIconClick(e, col)}
                                sx={{
                                  padding: "2px",
                                  "&:hover": { color: "#1976d2" },
                                }}
                              >
                                <FilterListIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent sx={{ width: 250, padding: 2 }}>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  value={tempColumnFilters[col] || ""}
                                  onChange={(e) =>
                                    setTempColumnFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                />
                                <DialogActions>
                                  <Button onClick={() => clearColumnFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyColumnFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + 1} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((row) => (
                      <TableRow key={row.aging_id}>
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFlag(row.aging_id, row.active_flag)
                              }
                              title="Toggle Flag"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    row.active_flag === 1
                                      ? "#2E9F50"
                                      : "#FF0C0C",
                                  width: 17,
                                  height: 17,
                                  transition: "color 0.2s",
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                              title="Edit"
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(row.aging_id)}
                              title="Delete"
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>
                        {visibleColumns.aging_id && (
                          <TableCell>{row.aging_id}</TableCell>
                        )}
                        {visibleColumns.client_name && (
                          <TableCell>{row.client_name}</TableCell>
                        )}
                        {visibleColumns.environment_name && (
                          <TableCell>{row.environment_name}</TableCell>
                        )}
                        {visibleColumns.application_name && (
                          <TableCell>{row.application_name}</TableCell>
                        )}
                        {visibleColumns.logical_group_name && (
                          <TableCell>{row.logical_group_name}</TableCell>
                        )}
                        {visibleColumns.aging_count && (
                          <TableCell>{row.aging_count}</TableCell>
                        )}
                        {visibleColumns.pkms_count && (
                          <TableCell>{row.pkms_count}</TableCell>
                        )}
                        {visibleColumns.ca_missing_count && (
                          <TableCell>{row.ca_missing_count}</TableCell>
                        )}
                        {visibleColumns.date && (
                          <TableCell>{formatDisplayDate(row.date)}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + 1} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No data found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Dialog
                open={openDeleteDialog}
                onClose={(event, reason) => {
                  if (reason === "backdropClick") return;
                  cancelDelete();
                }}
                maxWidth="xs"
                fullWidth
              >
                <DialogTitle sx={{ fontWeight: "bold", fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}>
                  Confirm Delete
                </DialogTitle>

                <DialogContent>
                  <Typography fontSize="0.9rem">
                    Are you sure you want to delete Aging ID{" "}
                    <strong>{deleteId}</strong> ?
                  </Typography>
                </DialogContent>

                <DialogActions>
                  <Button onClick={cancelDelete} size="small" sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}>
                    CANCEL
                  </Button>
                  <Button
                    onClick={confirmDelete}
                    variant="contained"
                    // color="error"
                    size="small"
                    sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}
                  >
                    CONFIRM
                  </Button>
                </DialogActions>
              </Dialog>
            </TableContainer>
          </Paper>
        </ThemeProvider>
      </Box>

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={settingsAnchorEl}
        open={Boolean(settingsAnchorEl)}
        onClose={() => setSettingsAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 260,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                px: 2,
                py: 1,
                border: "1px solid #ccc",
                borderRadius: 1.5,
                bgcolor: "#fff",
              }}
            >
              <Typography fontSize="0.8rem" fontWeight="bold">
                {columnLabels[col]}
              </Typography>
              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((p) => ({ ...p, [col]: e.target.checked }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Add/Edit Dialog */}
      <ThemeProvider theme={theme}>
        <Dialog
          open={openDialog}
          maxWidth="md"
          fullWidth
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDialog(false);
          }}
        >
          <DialogTitle>
            {isEditing ? "Edit Aging Record" : "Add Aging Record"}
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2, // tighter spacing
                mt: 1,
                alignItems: "center", // better vertical alignment than "start"
                // Force smaller & consistent sizing
                "& .MuiInputBase-root": {
                  height: 40,
                  minHeight: 40,
                  boxSizing: "border-box",
                },
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  paddingTop: 0,
                  paddingBottom: 0,
                  "& .MuiSelect-select": {
                    padding: "8.5px 14px", // compact padding
                    height: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "8.5px 14px",
                  fontSize: "0.875rem", // ~14px text
                },
                "& .MuiFormHelperText-root": {
                  marginTop: 2,
                  fontSize: "0.7rem",
                  lineHeight: 1.2,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                  transform: "translate(14px, 9px) scale(1)",
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(14px, -6px) scale(0.75)",
                  },
                },
              }}
            >
              {/* Row 1 */}
              <TextField
                label="Client"
                value={formData.client_name || "Loading..."}
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: "white",
                  "& .MuiInputBase-input": {
                    cursor: "not-allowed",
                    color: "grey",
                  },
                }}
                required
                fullWidth
                size="small"
              />

              <FormControl fullWidth error={!!formErrors.env_id}>
                <InputLabel required>Environment</InputLabel>
                <Select
                  value={formData.env_id}
                  label="Environment"
                  onChange={handleEnvironmentChange}
                  size="small"
                >
                  {environments.map((env) => (
                    <MenuItem
                      key={env.env_id}
                      value={env.env_id}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {env.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.env_id && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {formErrors.env_id}
                  </Typography>
                )}
              </FormControl>

              {/* Row 2 */}
              <FormControl fullWidth error={!!formErrors.app_id}>
                <InputLabel required>Application</InputLabel>
                <Select
                  value={formData.app_id}
                  label="Application"
                  onChange={handleApplicationChange}
                  disabled={!formData.env_id}
                  size="small"
                >
                  {filteredApplications.map((app) => (
                    <MenuItem
                      key={app.app_id}
                      value={app.app_id}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {app.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.app_id && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {formErrors.app_id}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth error={!!formErrors.lg_id}>
                <InputLabel required>Logical Group</InputLabel>
                <Select
                  value={formData.lg_id}
                  label="Logical Group"
                  onChange={handleLogicalGroupChange}
                  disabled={!formData.app_id}
                  size="small"
                >
                  {filteredLogicalGroups.map((lg) => (
                    <MenuItem
                      key={lg.lg_id}
                      value={lg.lg_id}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {lg.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.lg_id && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {formErrors.lg_id}
                  </Typography>
                )}
              </FormControl>

              {/* Row 3 */}
              <TextField
                label="Aging Count"
                type="number"
                value={formData.aging_count}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, aging_count: e.target.value }))
                }
                onKeyDown={(e) => {
                  // Prevent 'e', 'E', '+', '-' characters in number fields
                  if (['e', 'E', '+', '-'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                fullWidth
                size="small"
              />

              <TextField
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, date: e.target.value }));
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!formErrors.date}
                helperText={formErrors.date}
                size="small"
              />

              {/* Row 4 */}
              <TextField
                label="PKMS Count"
                type="number"
                value={formData.pkms_count}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, pkms_count: e.target.value }))
                }
                onKeyDown={(e) => {
                  // Prevent 'e', 'E', '+', '-' characters in number fields
                  if (['e', 'E', '+', '-'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                fullWidth
                size="small"
              />

              <TextField
                label="CA Missing Count"
                type="number"
                value={formData.ca_missing_count}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    ca_missing_count: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  // Prevent 'e', 'E', '+', '-' characters in number fields
                  if (['e', 'E', '+', '-'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                fullWidth
                size="small"
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} size="small">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              size="small"
              disabled={isEditing && !isFormChanged}
            >
              {isEditing ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      {/* Export Dialog - Fixed Layout */}
      {/* Export Dialog */}
      <Dialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1rem" }}>
          Export Aging Report
          <IconButton
            onClick={() => setOpenExportDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {exportError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {exportError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Date Row */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                value={exportFilters.date_from}
                onChange={(e) =>
                  setExportFilters((p) => ({
                    ...p,
                    date_from: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="End Date"
                type="date"
                value={exportFilters.date_to}
                onChange={(e) =>
                  setExportFilters((p) => ({
                    ...p,
                    date_to: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Environment & Application Row */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Environment</InputLabel>
                <Select
                  value={exportFilters.env_id}
                  label="Environment"
                  onChange={async (e) => {
                    const envId = e.target.value;
                    setExportFilters((p) => ({
                      ...p,
                      env_id: envId,
                      app_id: "",
                    }));

                    if (envId) {
                      await fetchApplicationsByEnvironment(envId);
                    } else {
                      setFilteredApplications([]);
                    }
                  }}
                >
                  {/* <MenuItem value="">All</MenuItem> */}
                  {environments.map((env) => (
                    <MenuItem key={env.env_id} value={env.env_id}>
                      {env.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Application</InputLabel>
                <Select
                  value={exportFilters.app_id}
                  label="Application"
                  disabled={!exportFilters.env_id}
                  onChange={(e) =>
                    setExportFilters((p) => ({
                      ...p,
                      app_id: e.target.value,
                    }))
                  }
                >
                  {/* <MenuItem value="">All</MenuItem> */}
                  {filteredApplications.map((app) => (
                    <MenuItem key={app.app_id} value={app.app_id}>
                      {app.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          {/* <Button
      onClick={() => setOpenExportDialog(false)}
      size="small"
    > */}
          <Button onClick={() => setOpenExportDialog(false)}>Cancel</Button>

          <Button
            variant="contained"
            // size="small"
            disabled={!exportFilters.date_from || !exportFilters.date_to}
            onClick={handleExport}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AgingDetails;
